import { Component, inject, Input, OnInit } from '@angular/core';
import { InputNumberModule } from 'primeng/inputnumber';
import { NgClass } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { TranslocoPipe } from '@jsverse/transloco';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { CallidusCurrencyPipe } from '@shared/pipes/currency/callidus-currency.pipe';
import { AdditionalCoverageBoxComponent } from '@shared/components/additional-coverage-box/additional-coverage-box.component';
import { SelectOption } from '@shared/models/select-option.model';
import { BvsHaftpflichtUndKaskoFormModel } from '../../models/form/bvs-haftpflicht-und-kasko-form.model';
import { PremiumDisplayComponent } from '@shared/components/premium-display/premium-display.component';
import { CalculationErrorDisplayComponent } from '@shared/components/calculation-error-display/calculation-error-display.component';
import { AdditionalCoverageBaseComponent } from '@shared/directives/additional-coverage-base-component.directive';
import { AdditionalCoverageBoxForEnumComponent } from '@shared/components/additional-coverage-for-enum/additional-coverage-box-for-enum.component';
import { environment } from '@environment/environment';
import {
  CalculationResultStatus,
  BoatType,
  CallidusBvsHaftpflichtBoatInformation,
  CallidusBvsHaftpflichtFigures,
  CallidusBvsHaftpflichtUndKaskoBoatInformation,
  CallidusBvsHaftpflichtUndKaskoFigures,
  CallidusBvsHaftpflichtUndKaskoMoeglicheSelbstbehalteResultDto,
  CallidusBvsSelbstbehaltKunde,
  CallidusBvsVersicherungspraemieHaftpflichtUndKaskoCalcDto,
  VersicherungspraemieCalculationResultDto
} from '@backend/models';
import { BvsHaftpflichtStateService } from '@shared/services/bvs-haftpflicht-state.service';
import { isFormGroupInvalidExcept } from '@shared/utils/form.util';

@Component({
  selector: 'app-additional-coverage-haftpflicht-kasko-data',
  imports: [
    InputNumberModule,
    PaginatorModule,
    TranslocoPipe,
    ReactiveFormsModule,
    PremiumDisplayComponent,
    AdditionalCoverageBoxComponent,
    CalculationErrorDisplayComponent,
    NgClass,
    AdditionalCoverageBoxForEnumComponent
  ],
  providers: [CallidusCurrencyPipe],
  templateUrl: './additional-coverage-haftpflicht-kasko-data.component.html',
  styleUrl: './additional-coverage-haftpflicht-kasko-data.component.scss'
})
export class AdditionalCoverageHaftpflichtKaskoDataComponent extends AdditionalCoverageBaseComponent implements OnInit {
  @Input() form!: FormGroup<BvsHaftpflichtUndKaskoFormModel>;

  private readonly bvsHaftpflichtStateService = inject(BvsHaftpflichtStateService);

  filteredSelectOptions: SelectOption<CallidusBvsSelbstbehaltKunde>[] = [];
  showCalculationPremiumEnabled = environment.featureToggles.showCalculationPremiumEnabled;

  selectOptions: SelectOption<CallidusBvsSelbstbehaltKunde>[] = [
    { value: CallidusBvsSelbstbehaltKunde.Sb500, display: this.currencyPipe.transform(500, 'symbol') },
    { value: CallidusBvsSelbstbehaltKunde.Sb1000, display: this.currencyPipe.transform(1000, 'symbol') },
    { value: CallidusBvsSelbstbehaltKunde.Sb2500, display: this.currencyPipe.transform(2500, 'symbol') },
    { value: CallidusBvsSelbstbehaltKunde.Sb5000, display: this.currencyPipe.transform(5000, 'symbol') },
    { value: CallidusBvsSelbstbehaltKunde.Sb7500, display: this.currencyPipe.transform(7500, 'symbol') },
    { value: CallidusBvsSelbstbehaltKunde.Sb10000, display: this.currencyPipe.transform(10000, 'symbol') }
  ];

  ngOnInit(): void {
    this.calculate();

    this.form.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.calculate());

    this.getMoeglicheSelbstbehalte();
  }

  calculate(): void {
    if (this.isFormInvalidForPremiumCalculation()) {
      return;
    }

    const getBvsVersicherungsPraemieCommand = this.createGetBvsVersicherungsPraemieCommand();

    this.bvsHaftpflichtStateService
      .calculateHaftpflichtUndKaskoPraemie(getBvsVersicherungsPraemieCommand)
      .subscribe((calculationResult: VersicherungspraemieCalculationResultDto) => this.calculationResult = calculationResult);
  }

  showInsurancePremium(): boolean {
    return !this.isFormInvalidForPremiumCalculation() &&
      this.calculationResult !== null &&
      (this.calculationResult.publicCalculationResultStatus === CalculationResultStatus.Ok ||
        this.calculationResult.publicCalculationResultStatus === CalculationResultStatus.OkWithWarning);
  }

  showErrorMessages(): boolean {
    return this.isFormInvalidForPremiumCalculation() ||

      (this.calculationResult !== null &&
        (this.calculationResult.publicCalculationResultStatus === CalculationResultStatus.OkWithWarning ||
          this.calculationResult.publicCalculationResultStatus === CalculationResultStatus.Error));

  }

  isBoatLessThan10YearsOld(): boolean {
    return !!this.form.controls.bvsBoatInformation.controls.constructionYear.value
      && (new Date().getFullYear() - this.form.controls.bvsBoatInformation.controls.constructionYear.value < 10);
  }

  isSailingBoat(): boolean {
    return this.form.controls.bvsBoatInformation.value.boatType === BoatType.SegelbootSegelyacht;
  }

  isFormInvalidForPremiumCalculation(): boolean {
    return isFormGroupInvalidExcept(this.form, ['customerData']);
  }

  createGetBvsVersicherungsPraemieCommand(): CallidusBvsVersicherungspraemieHaftpflichtUndKaskoCalcDto {
    const boatInfo = this.form.controls.bvsBoatInformation.controls;
    const figures = this.form.controls.bvsFigures.controls;

    return {
      callidusBvsHaftpflichtUndKaskoBoatInformation: {
        callidusBvsHaftpflichtBoatInformation: {
          boatType: boatInfo.boatType.value,
          boatConstructionType: boatInfo.boatConstructionType.value ?? undefined,
          manufacturer: boatInfo.manufacturer.value,
          yachtType: boatInfo.yachtType.value,
          boatModel: boatInfo.boatModel.value,
          boatYard: boatInfo.boatYard.value,
          fullWidth: boatInfo.fullWidth.value,
          fullLength: boatInfo.fullLength.value,
          isEnginePresent: boatInfo.isEnginePresent.value ?? false,
          engineManufacturer: boatInfo.engineManufacturer.value,
          engineModel: boatInfo.engineModel.value,
          engineFuelType: boatInfo.engineFuelType.value,
          engineHp: boatInfo.engineHp.value,
          builtInEngineCount: boatInfo.builtInEngineCount.value,
          builtInEngineHp: boatInfo.builtInEngineHp.value,
          extraEngineCount: boatInfo.extraEngineCount.value,
          extraEngineHp: boatInfo.extraEngineHp.value,
          mooringSummer: boatInfo.mooringSummer.value,
          mooringWinter: boatInfo.mooringWinter.value,
          isGutachtenPresent: boatInfo.isGutachtenPresent.value ?? false,
          mastMaterial: boatInfo.mastMaterial.value ?? undefined,
          sailingArea: boatInfo.sailingArea.value,
          countryRegistered: boatInfo.countryRegistered.value,
          isComfortAusstattungVorhanden: boatInfo.isComfortAusstattungVorhanden.value ?? false,
          isSailPresent: boatInfo.isSailPresent.value ?? false,
          sailSurfaceInMetersSquared: boatInfo.sailSurfaceInMetersSquared.value,
          maxKnotsOver40: boatInfo.maxKnotsOver40.value ?? false,
          isBeibootVorhanden: boatInfo.isBeibootVorhanden.value ?? false,
          beibootHp: boatInfo.beibootHp.value
        } as CallidusBvsHaftpflichtBoatInformation,
        constructionYear: boatInfo.constructionYear.value,
        isRegattateilnahmeSelected: boatInfo.isSport.value ?? false,
        hullMaterial: boatInfo.hullMaterial.value,
        isModifikationAmBootSelected: boatInfo.isBoatModified.value ?? false,
        boatModifiedValue: boatInfo.boatModifiedValue.value
      } as CallidusBvsHaftpflichtUndKaskoBoatInformation,
      callidusBvsHaftpflichtUndKaskoFigures: {
        callidusBvsHaftpflichtFigures: {
          isRechtsschutzSelected: figures.isRechtsschutzSelected.value ?? false,
          isKeinSelbstbehaltBeiRechtsschutzSelected: figures.isKeinSelbstbehaltBeiRechtsschutzSelected.value ?? false,
          isCharterSelected: figures.isCharterSelected.value ?? false,
          callidusBvsVersicherungssummeKunde: figures.callidusBvsVersicherungssummeKunde.value
        } as CallidusBvsHaftpflichtFigures,
        selbstbehaltKunde: figures.selbstbehaltKunde.value ?? CallidusBvsSelbstbehaltKunde.Sb500,
        isMaschinendeckungSelected: figures.isMaschinendeckung.value ?? false,
        wertDesBootesInEur: figures.insuranceSumBoat.value,
        wertDerZusaetzlichenAusruestungInEur: figures.insuranceSumAdditionalEquipment.value,
        wertDerPersoenlichenEffekteInEur: figures.insuranceSumPersoenlicheEffekte.value,
        wertDesBeibootsInEur: figures.insuranceSumDinghy.value,
        wertDesTrailersInEur: figures.insuranceSumTrailer.value,
        gesamtVersicherungssummeInEur: figures.totalInsuranceSum.value,
        isAlleWerteMitMwstSelected: figures.allPricesWithVAT.value ?? false
      } as CallidusBvsHaftpflichtUndKaskoFigures
    };
  }

  getMoeglicheSelbstbehalte(): void {
    const getBvsVersicherungsPraemieCommand: CallidusBvsVersicherungspraemieHaftpflichtUndKaskoCalcDto = this.createGetBvsVersicherungsPraemieCommand();

    this.bvsHaftpflichtStateService
      .getMoeglicheSelbstbehalteHaftpflichtUndKasko(getBvsVersicherungsPraemieCommand)
      .subscribe(selbstbehalte => this.handleGetMoegelicheSelbstbehalte(selbstbehalte));
  }

  handleGetMoegelicheSelbstbehalte(sbs: CallidusBvsHaftpflichtUndKaskoMoeglicheSelbstbehalteResultDto): void {
    const selbstbehalte = sbs.moeglicheSelbstbehalte;

    this.filteredSelectOptions = this.selectOptions.filter(option => selbstbehalte?.includes(option.value));

    if (this.filteredSelectOptions.length) {
      this.form.controls.bvsFigures.controls.selbstbehaltKunde.enable();
    } else {
      this.form.controls.bvsFigures.controls.selbstbehaltKunde.disable();
    }
  }
}
